import { HttpStatusCode } from 'axios';
import { createContext, ReactNode, useEffect, useReducer } from 'react';
import { Role } from 'src/Api/Enum/Role';
import { UserMeDto } from 'src/Api/Dto/UserMeDto';
import { rolePermission } from 'src/pages/config/permission/rolePermission';
import { PATH_AUTH, PATH_PAGE, PATH_PROFILE, PATH_WELCOME_PAGE, isAuthPath } from 'src/routes/paths';
import { getBgColor, mergePermission } from 'src/utils/general';
import { ActionMap, AuthState, AuthUser, JWTContextType } from '../@types/auth';
import { blimpAxiosClient, setTokenExp, setImpersonationExp } from '../utils/axiosInstances';
import { CustomerPermission } from 'src/pages/config/permission/permission';

// ─── Types ────────────────────────────────────────────────────────────────────

enum Types {
  Initial = 'INITIALIZE',
  Login = 'LOGIN',
  Logout = 'LOGOUT',
  Register = 'REGISTER',
  Update = 'UPDATE',
}

type JWTAuthPayload = {
  [Types.Initial]: { isAuthenticated: boolean; user: AuthUser };
  [Types.Login]: undefined;
  [Types.Logout]: undefined;
  [Types.Register]: { user: AuthUser };
  [Types.Update]: { user: AuthUser };
};

type JWTActions = ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>];

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

// ─── Reducer ──────────────────────────────────────────────────────────────────

const JWTReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case Types.Initial:
      return { isAuthenticated: action.payload.isAuthenticated, isInitialized: true, user: action.payload.user };
    case Types.Login:
      return { ...state, isAuthenticated: true };
    case Types.Logout:
      return { ...state, isAuthenticated: false, user: null };
    case Types.Register:
      return { ...state, isAuthenticated: true, user: action.payload.user };
    case Types.Update:
      return { ...state, isAuthenticated: true, user: action.payload.user };
    default:
      return state;
  }
};


// ─── Pure async helpers ───────────────────────────────────────────────────────

/**
 * Fetches the full user profile (roles, permissions, customer features) from the business API.
 * Requires a valid auth cookie — must only be called after a successful login or /me check.
 */
const fetchUserWithPermissions = async (userId: string): Promise<AuthUser> => {
  const response = await blimpAxiosClient.api.get(`/users/${userId}`);
  if (response.status !== HttpStatusCode.Ok) throw new Error('User fetch failed');

  const user = response.data;
  const role: Role | undefined = user?.roles?.[0] as Role;
  const rolePerm = role ? rolePermission[role] : undefined;
  const { data: customerFeatures } = await blimpAxiosClient.api.get(`/customers/${user.customerId}/features`);

  if (user.customer.parentCustomerId) {
    const parentCustomer = await blimpAxiosClient.api.get(`/customers/${user.customerId}/parent`);
    user.parentLogo = parentCustomer.data.logo;
  }

  const parsedFeatures = customerFeatures.map((f: CustomerPermission) => ({
    ...f,
    metadata: typeof f.metadata === 'string' ? JSON.parse(f.metadata) : f.metadata,
  }));

  const userPermission = rolePerm ? rolePerm(parsedFeatures, user.roles) : {};
  user.permission = mergePermission(userPermission, parsedFeatures, user.roles);
  user.bgColor = getBgColor(user.customer?.banner);
  user.customerFeatures = parsedFeatures;
  return user;
};

/**
 * Single entry point for loading the authenticated user into React state.
 * Calls GET /auth/me to get the identity claims, then enriches with permissions.
 * Throws if either request fails — callers should treat a thrown error as "not authenticated".
 */
export const loadAuthUser = async (): Promise<AuthUser> => {

  let rawRes;
  try {
    rawRes = await blimpAxiosClient.auth.get('/me', { validateStatus: (s) => s === 200 });
  } catch (err: any) {
    if (err?.response?.status !== 401) throw err;
    // Access token expired → try refresh + retry /me once
    await blimpAxiosClient.auth.post('/refresh');
    rawRes = await blimpAxiosClient.auth.get('/me', { validateStatus: (s) => s === 200 });
  }
  const raw = rawRes.data;
  const me: UserMeDto = raw.originalUser
    ? { ...raw, isImpersonating: true as const }
    : { ...raw, isImpersonating: false as const };

  // Set _tokenExp before api calls — proactive refresh interceptor needs it
  // When impersonating, use originalUser.exp (that token is what /refresh renews)
  setTokenExp(me.isImpersonating ? me.originalUser.exp : me.user.exp);

  setImpersonationExp(me.isImpersonating ? me.user.exp : undefined);

  const user = await fetchUserWithPermissions(me.user.userId);

  if (user && me.isImpersonating) {
    user.impersonation = {
      originalUserId: me.originalUser.userId,
      originalUserName: me.originalUser.username,
    };
  } else if (user) {
    user.impersonation = undefined;
  }

  return user;
};

// ─── MFA error class ─────────────────────────────────────────────────────────

export class MfaRequiredError extends Error {
  constructor() {
    super('mfa_required');
    this.name = 'MfaRequiredError';
  }
}

// ─── Context & Provider ───────────────────────────────────────────────────────

const AuthContext = createContext<JWTContextType | null>(null);

type AuthProviderProps = { children: ReactNode };

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(JWTReducer, initialState);

  const logout = async () => {
    // Best-effort: clear the server-side cookie; ignore errors (e.g. already expired)
    try { await blimpAxiosClient.auth.post('/logout'); } catch { /* ignore */ }
    localStorage.removeItem('storeMonitor.lastArea');
    setImpersonationExp(undefined);
    dispatch({ type: Types.Logout });
    window.location.href = PATH_AUTH.login;
  };

  const completeLogin = async () => {
    await blimpAxiosClient.auth.get('/xsrf');
    const user = await loadAuthUser();
    dispatch({ type: Types.Initial, payload: { isAuthenticated: true, user } });
  };

  useEffect(() => {
    const initialize = async () => {
      // Skip /me on auth pages — no session expected, avoids 401 noise after logout
      if (isAuthPath(window.location.pathname)) {
        dispatch({ type: Types.Initial, payload: { isAuthenticated: false, user: null } });
        return;
      }
      try {
        const user = await loadAuthUser();
        dispatch({ type: Types.Initial, payload: { isAuthenticated: true, user } });
      } catch {
        dispatch({ type: Types.Initial, payload: { isAuthenticated: false, user: null } });
      }
    };

    initialize();
  }, []);

  /**
   * Username/password login.
   * 200 OK  → session cookie set; loads user via /me.
   * 202     → MFA required; throws MfaRequiredError so the caller can navigate to /mfa.
   */
  const login = async (username: string, password: string) => {
    if (username === 'blimp@blimp.com') { window.location.href = PATH_PAGE.page401; return; }

    const response = await blimpAxiosClient.auth.post(
      '/login',
      { username, password },
      { validateStatus: (s) => s === 200 || s === 202 },
    );

    if (response.status === 202) throw new MfaRequiredError();

    await completeLogin();
  };

  const updateUser = async (user: AuthUser) => {
    if (!user) { window.location.href = PATH_PROFILE.user; return; }

    const role: Role | undefined = user?.roles?.[0] as Role;
    const rolePerm = role ? rolePermission[role] : undefined;
    const { data: customerFeatures } = await blimpAxiosClient.api.get(`/customers/${user.customerId}/features`);
    const userPermission = rolePerm ? rolePerm(customerFeatures, user.roles) : {};
    user.permission = mergePermission(userPermission, customerFeatures, user.roles);

    dispatch({ type: Types.Update, payload: { user } });
  };

  /**
   * Re-fetches /me and refreshes the user in state.
   * Called on tab focus (visibilitychange) to sync impersonation state across tabs.
   * If the session is dead, performs a clean logout.
   */
  const refreshUser = async () => {
    try {
      const wasImpersonating = !!state.user?.['impersonation'];
      const user = await loadAuthUser();
      const isNowImpersonating = !!user?.['impersonation'];

      dispatch({ type: Types.Update, payload: { user } });

      // Ricarica solo se lo stato di impersonazione è cambiato
      if (wasImpersonating !== isNowImpersonating) {
        window.location.replace(PATH_WELCOME_PAGE); 
      }
    } catch {
      logout();
    }
  };

  const register = async (_email: string, _password: string, _firstName: string, _lastName: string) => {
    // TODO: implement with cookie auth — registration endpoint not yet defined
    // Previous implementation used JWT tokens which are no longer supported.
    throw new Error('register() not implemented for cookie auth');
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        completeLogin,
        logout,
        register,
        updateUser,
        refreshUser,
        isAdmin: state.user?.roles?.some((role: string) => role === 'ADMIN') ?? false,
        isSuperUser: state.user?.roles?.some((role: string) => role === 'SUPER_USER') ?? false,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };